import React from 'react'
import posthog from 'posthog-js'
import UserAvatar from './UserAvatar'
import mixpanel from 'mixpanel-browser'
import { useDispatch } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import { clearUserInfo } from '@/store/features/userSlice'
import { Gamepad2, LogOut, Trophy, User } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@radix-ui/react-dropdown-menu'
import cookies from '@/utils/cookies'

const MAIN_SITE = import.meta.env.VITE_MAIN_SITE_URL

const menuItems = [
  { icon: User, label: 'Profile', path: `${MAIN_SITE}/profile` },
  { icon: Gamepad2, label: 'My Games', path: '/dashboard/my-games' },
  { icon: Trophy, label: 'LeaderBoard', path: '/dashboard/leaderboard' },
]

interface AvatarWithDropdownProps {
  userInfo: {
    name: string
    email: string
    picture: string
  } | null
}

const AvatarWithDropdown: React.FC<AvatarWithDropdownProps> = ({
  userInfo,
}) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const profilePicture = userInfo?.picture
    ? decodeURIComponent(userInfo.picture).replace(/=s96-c/, '=s200-c')
    : undefined

  const handleLogout = () => {
    posthog.capture('user_logged_out', {
      user_email: userInfo?.email,
    })
    dispatch(clearUserInfo())
    cookies.remove('ai_tk')
    posthog.reset(true)
    mixpanel.reset()
    navigate('/login')
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="focus:outline-none">
          <UserAvatar imageUrl={profilePicture} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="z-50 mr-4 mt-2 rounded-lg border border-white/10 bg-background text-white shadow-lg backdrop-blur-lg">
        <div className="flex items-center p-2">
          <UserAvatar className="mr-2" imageUrl={profilePicture} />
          <div className="flex-1">
            <p className="font-semibold text-white">
              {userInfo?.name || 'User'}
            </p>
            <p
              className="max-w-[180px] truncate text-xs text-gray-400"
              title={userInfo?.email || 'No email provided'}
            >
              {userInfo?.email || 'No email provided'}
            </p>
          </div>
        </div>
        <DropdownMenuSeparator className="my-1 h-px w-full bg-white/10" />
        {menuItems.map((item, index) => (
          <DropdownMenuItem
            asChild
            key={index}
            className="flex cursor-pointer items-center rounded-lg p-2 text-sm text-white transition-colors duration-300 hover:outline-0 focus-visible:outline-0"
          >
            <Link to={`${item.path}`}>
              <item.icon className="mr-2 size-4" />
              {item.label}
            </Link>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator className="my-1 h-px w-full bg-white/10" />
        <DropdownMenuItem
          className="flex cursor-pointer items-center rounded-lg p-2 text-sm text-red-500 transition-colors duration-300 hover:outline-0 focus-visible:outline-0"
          onClick={handleLogout}
        >
          <LogOut className="mr-2 size-4" />
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default AvatarWithDropdown
